import { useState } from 'react';
import type { ReactElement } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSession } from '../lib/session.js';
import { NavIcon } from './NavIcon.js';
import { NAV_ITEMS, sectionTitle } from './navigation.js';

export function AppShell(): ReactElement {
  const { user, isPersistent, signOut } = useSession();
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = (): void => {
    setSigningOut(true);
    void signOut().finally(() => setSigningOut(false));
  };

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <span className="brand-mark" aria-hidden="true">
            PB
          </span>
          <span className="brand-name">
            Pizza Bros <span className="brand-name__accent">TV</span>
          </span>
        </div>

        <nav className="sidebar__nav" aria-label="Sections">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) => (isActive ? 'nav-link nav-link--active' : 'nav-link')}
            >
              <NavIcon name={item.icon} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <p className="sidebar__foot">In-store screen management</p>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <h1 className="topbar__title">{sectionTitle(location.pathname)}</h1>

          <div className="topbar__user">
            {user !== null ? (
              <div className="user-summary">
                <span className="user-summary__name">{user.name || user.email}</span>
                <span className="user-summary__meta">
                  <span className={`chip chip--${user.role === 'admin' ? 'accent' : 'neutral'}`}>
                    {user.role === 'admin' ? 'Admin' : 'Store manager'}
                  </span>
                  <span className="user-summary__email">{user.email}</span>
                </span>
              </div>
            ) : null}
            <button
              type="button"
              className="button button--ghost"
              onClick={handleSignOut}
              disabled={signingOut}
            >
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </header>

        {!isPersistent ? (
          <div className="banner banner--warning" role="status">
            This browser will not keep the session between page loads, so you will be asked to sign
            in again after a reload.
          </div>
        ) : null}

        <main className="page">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
